/* eslint-disable import/no-cycle */
//This component calls an API on each key stroke, and shows the returned courses for user to choose between them.
import React, { forwardRef, useImperativeHandle, useState } from "react";
import axios from "axios";
import Cookies from 'js-cookie'; 
import { Box, List, TextField, Autocomplete, ListItem, Typography, SxProps, useTheme, Paper } from "@mui/material";
import favoriteCourse from "../utlis/favoriteCourse";
import FavoriteButton from "./favoriteButton";

export interface ICourse {
  name: string;
  prerequisiteHours: number;
  code: string;
  credits: number;
  favorite?: boolean;
}

interface IDataListProps {
  onChoose: (chosenCourse: ICourse) => void;
  bylaw: string;
  department: string; 
  style?: SxProps;
}

const DataList = forwardRef((props: IDataListProps, ref) => {
  const { onChoose, bylaw,department, style } = props;
  
  const [ favCourses, setFavCourses ] = React.useState(JSON.parse(Cookies.get('favCourses') ?? '[]'));
  const [options, setOptions] = React.useState<ICourse[]>([...favCourses]);
  const [selectedCourse, setSelectedCourse] = useState<ICourse | null>(null); 
  const theme = useTheme();

  const onChooseCourse = (e:React.SyntheticEvent<Element, Event>, option: ICourse) => {
    onChoose(option);
    setSelectedCourse(option);
    // Close the virtual keyboard by blurring focus from a dummy input field
    const target = e.target as HTMLElement;
    if (target) {
      target.blur();
    }
  };

  const onFavoriteClick = (event: React.MouseEvent<SVGElement, MouseEvent>, course: ICourse ) => {
    const favoriteCourses = favoriteCourse(event, course)
    setFavCourses(favoriteCourses);
  }

  // const fetchDataFromAPI = async (searchQuery: string) => {
  //   try {
  //     const response = bylaw === 'New' ? await axios.get(`https://credithub.onrender.com/api/courses/search/${searchQuery}?bylaws=2023`) :
  //       await axios.get(`https://credithub.onrender.com/api/courses/search/${searchQuery}?bylaws=2018`)
      
  //     const sortedData = response.data.sort((a: ICourse, b: ICourse) => {
  //       if (b.favorite && !a.favorite) return 1;
  //       if (a.favorite && !b.favorite) return -1;
  //       return a.code.localeCompare(b.code);
  //     });

  //     response.data.forEach((course: ICourse) => {
  //       if (favCourses.some((favCourse: ICourse) => favCourse.code === course.code))
  //         course.favorite = true;
  //     })

  //     const combinedCourses = [...favCourses, ...sortedData];
  //     const uniqueCourses = Array.from(new Map(combinedCourses.map((course) => [course.code, course])).values());

  //     setOptions([...uniqueCourses]);
  //   } 
  //   catch (error) { setOptions([...favCourses]); }
  // };
  const fetchDataFromAPI = async () => {
    try {
      const response =  bylaw === 'New' ? await axios.get(`https://mychs-3aag.vercel.app/department/${department}`): await axios.get(`https://mychs-3aag.vercel.app/department/${department}old`);
      const departmentCourses = response.data;
      
      // Mark courses as favorite if they exist in favCourses
      departmentCourses.forEach((course: ICourse) => {
        if (favCourses.some((favCourse: ICourse) => favCourse.code === course.code))
          course.favorite = true;
      });

      // Combine favorite courses with department courses, ensuring no duplicates
      const combinedCourses = [...favCourses, ...departmentCourses];
      const uniqueCourses = Array.from(new Map(combinedCourses.map((course) => [course.code, course])).values());

      setOptions([...uniqueCourses]);
    } 
    catch (error) { setOptions([...favCourses]); }
  };


  const onClear = () => {
    const close = document.getElementsByClassName("MuiAutocomplete-clearIndicator")[0] as HTMLElement | null;
    if (close) close.click();
    setOptions([...favCourses]);
    setSelectedCourse(null);
  };

  useImperativeHandle(ref, () => ({ onClear }));

  return (
    <Autocomplete
      disablePortal
      blurOnSelect={'touch'}
      options={options.sort((a: ICourse, b: ICourse) => {
        if (b.favorite && !a.favorite) return 1;
        if (a.favorite && !b.favorite) return -1;
        return a.code.localeCompare(b.code);
      })}
      groupBy={ (option) => option.favorite ? 'Favorites' :  option.code.slice(0, 3)}
      getOptionLabel={(option) => option.code + ": " + option.name }
      onChange={(e, chosenCourse) => chosenCourse ?  onChooseCourse(e,chosenCourse) :  null}
      onClick={() => fetchDataFromAPI()}
      onFocus={() => fetchDataFromAPI()} 

      renderInput={(params) => (<TextField {...params}/>)}
      renderOption={(props, option) => {
        return(
          <ListItem
            id='course'
            sx={{ 
              padding: '4px 10px 4px 20px', 
              cursor: 'pointer', 
              background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor,
            }} {...props}>
            <Box id={'course'} sx={{display:'flex', justifyContent:'space-between', width:'100%', alignItems:'center'}}>
              <Typography id='course' sx={{width:'90%'}}>{option.code}: {option.name}</Typography>

              <FavoriteButton
                fill={favCourses.some((course: ICourse)=> course.code === option.code ) ? 'yellow' : 'white'}
                onFavoriteClick={(e: React.MouseEvent<SVGElement, MouseEvent>) => onFavoriteClick(e, option)}
              />
            </Box>
          </ListItem>
        )
      }}
      renderGroup={(params) => (
        <li key={params.group}>
          <Box 
            sx={{ 
              padding: '4px 10px', 
              color: 'black', 
              fontWeight: 500,
              background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor
            }}>
            {params.group}
          </Box>
          <List 
            sx={{ 
              padding: '0', 
              color: 'black', 
              background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor,
              ':hover': { background: bylaw === 'Old' ? theme.palette.common.oldBylawHoverColor : theme.palette.common.newBylawHoverColor }
            }}>
            {params.children}
          </List>
        </li>
      )}
      PaperComponent={(props) => (
        <Paper sx={{ background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor}} {...props}/>
      )}
      sx={{
        flex: 1,
        fontFamily: 'Oswald',
        boxShadow: "none",
        background: bylaw === 'Old' ? theme.palette.common.oldBylawColor : theme.palette.common.newBylawColor,
        ".MuiOutlinedInput-notchedOutline": { border: '1px solid black'},
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":{ border: '1px solid black' },
        "& .MuiAutocomplete-clearIndicator":{ display:'none' },
        "& .MuiAutocomplete-popperDisablePortal": {
          backgroundColor: 'red !important'
        },
        "& .MuiInputBase-root": {
          fontFamily: 'inherit',
          height: "35px !important",
          padding: '0px 0px 0px 5px',

        },
  
        ...style
      }}
    />
  );
});
export default DataList;